import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './store/authStore';
import { authService } from './services/authService';
import { TenantProvider } from './contexts/TenantContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import GamesPage from './pages/GamesPage';
import GamePlayPage from './pages/GamePlayPage';
import ProgressPage from './pages/ProgressPage';
import ProfilePage from './pages/ProfilePage';
import DailyPage from './pages/DailyPage';
import ProfessionalDashboardPage from './pages/ProfessionalDashboardPage';
import ProfessionalStatsPage from './pages/ProfessionalStatsPage';
import ProfessionalPatientsPage from './pages/ProfessionalPatientsPage';
import ProfessionalSessionsPage from './pages/ProfessionalSessionsPage';
import CreateSessionPage from './pages/CreateSessionPage';
import SessionDetailPage from './pages/SessionDetailPage';
import ActivePresencialSessionPage from './pages/ActivePresencialSessionPage';
import PatientSessionsPage from './pages/PatientSessionsPage';
import PatientSessionDetailPage from './pages/PatientSessionDetailPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminStatsPage from './pages/AdminStatsPage';
import UserStatsPage from './pages/UserStatsPage';

// Static Pages
import AboutPage from './pages/static/AboutPage';
import ContactPage from './pages/static/ContactPage';
import PrivacyPage from './pages/static/PrivacyPage';
import TermsPage from './pages/static/TermsPage';

// Herramientas (logopedia/rehabilitación)
import HerramientasLibraryPage from './pages/herramientas/HerramientasLibraryPage';
import HerramientasCreatePage from './pages/herramientas/HerramientasCreatePage';
import HerramientasEditPage from './pages/herramientas/HerramientasEditPage';
import HerramientasSessionPage from './pages/herramientas/HerramientasSessionPage';
import HerramientasFichasPage from './pages/herramientas/HerramientasFichasPage';

// Kids Pages
import KidProfilesPage from './pages/kids/KidProfilesPage';
import KidSelectorPage from './pages/kids/KidSelectorPage';
import KidHomePage from './pages/kids/KidHomePage';

// Games
import LightUpGame from './pages/games/LightUpGame';
import ThermometersGame from './pages/games/ThermometersGame';
import BricksGame from './pages/games/BricksGame';
import HitoriGame from './pages/games/HitoriGame';
import KakurasuGame from './pages/games/KakurasuGame';
import BinaryGame from './pages/games/BinaryGame';
import CampingGame from './pages/games/CampingGame';
import TowersGame from './pages/games/TowersGame';
import FutoshikiGame from './pages/games/FutoshikiGame';
import NurikabeGame from './pages/games/NurikabeGame';
import SuguruGame from './pages/games/SuguruGame';
import MastermindGame from './pages/games/MastermindGame';
import DuetoGame from './pages/games/DuetoGame';
import ConvergenciaGame from './pages/games/ConvergenciaGame';
import FlechasGame from './pages/games/FlechasGame';
import VecinosGame from './pages/games/VecinosGame';
import ITVExpressGame from './pages/games/ITVExpressGame';
import SashiganeGame from './pages/games/SashiganeGame';
import TriasGame from './pages/games/TriasGame';
import CineGame from './pages/games/CineGame';

// Kids Games
import MemoriaGame from './pages/games/MemoriaGame';
import SimonGame from './pages/games/SimonGame';
import MastermindKidsGame from './pages/games/MastermindKidsGame';
import SudokuKidsGame from './pages/games/SudokuKidsGame';
import LaberintoGame from './pages/games/LaberintoGame';
import DiferenciasGame from './pages/games/DiferenciasGame';
import TetravexGame from './pages/games/TetravexGame';
import FritoVariadoGame from './pages/games/FritoVariadoGame';
import TasukueaGame from './pages/games/TasukueaGame';
import SenderosGame from './pages/games/SenderosGame';
import NorinoriGame from './pages/games/NorinoriGame';
import NexoGame from './pages/games/NexoGame';
import TetonorGame from './pages/games/TetonorGame';
import FillominoGame from './pages/games/FillominoGame';
import EstanqueGame from './pages/games/EstanqueGame';

// Protected Route wrapper
/**
 * Comprueba contra el servidor, una vez al arrancar, que la sesión guardada
 * sigue viva.
 *
 * Hace falta porque `isAuthenticated` se persiste en localStorage y no caduca
 * nunca, mientras el token del servidor dura 7 días. Sin esta comprobación, un
 * usuario que vuelve pasada una semana entra como si nada, juega una partida
 * entera y el guardado falla con 401: las páginas de juego son públicas, así
 * que nada le obligaba antes a identificarse de nuevo.
 *
 * Un 401 lo gestiona el interceptor de `api.ts` (limpia el store y lleva al
 * login). Un fallo de red NO cierra la sesión: no sabríamos si el token es
 * malo o si es el servidor el que no contesta.
 */
function ValidacionDeSesion() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const yaComprobada = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || yaComprobada.current) return;
    yaComprobada.current = true;

    authService
      .me()
      .then(({ user, profile }) => useAuthStore.getState().setUser(user, profile ?? null))
      .catch(() => {
        // 401: ya lo ha gestionado el interceptor. Otro error: se ignora.
      });
  }, [isAuthenticated]);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <AccessibilityProvider>
    <TenantProvider>
      <BrowserRouter>
        <ValidacionDeSesion />
        <Routes>
          {/* Rutas públicas */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<HomePage />} />
          </Route>

          {/* Páginas estáticas (públicas) */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Rutas de autenticación */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Rutas protegidas */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            {/* Kids Routes - Panel de gestión para padres */}
            <Route path="kids" element={<KidProfilesPage />} />
            <Route path="kids/selector" element={<KidSelectorPage />} />
            <Route path="kids/home" element={<KidHomePage />} />

            <Route path="juegos" element={<GamesPage />} />
            <Route path="juego/:gameCode" element={<GamePlayPage />} />
            <Route path="juegos/bombillas" element={<LightUpGame />} />
            <Route path="juegos/termometros" element={<ThermometersGame />} />
            <Route path="juegos/ladrillos" element={<BricksGame />} />
            <Route path="juegos/hitori" element={<HitoriGame />} />
            <Route path="juegos/kakurasu/:dificultad" element={<KakurasuGame />} />
            <Route path="juegos/kakurasu" element={<Navigate to="/juegos/kakurasu/1" replace />} />
            <Route path="juegos/binary/:dificultad" element={<BinaryGame />} />
            <Route path="juegos/binary" element={<Navigate to="/juegos/binary/1" replace />} />
            <Route path="juegos/camping/:dificultad" element={<CampingGame />} />
            <Route path="juegos/camping" element={<Navigate to="/juegos/camping/1" replace />} />
            <Route path="juegos/torres/:dificultad" element={<TowersGame />} />
            <Route path="juegos/torres" element={<Navigate to="/juegos/torres/1" replace />} />
            <Route path="juegos/futoshiki/:dificultad" element={<FutoshikiGame />} />
            <Route path="juegos/futoshiki" element={<Navigate to="/juegos/futoshiki/1" replace />} />
            <Route path="juegos/nurikabe/:dificultad" element={<NurikabeGame />} />
            <Route path="juegos/nurikabe" element={<Navigate to="/juegos/nurikabe/1" replace />} />
            <Route path="juegos/suguru/:dificultad" element={<SuguruGame />} />
            <Route path="juegos/suguru" element={<Navigate to="/juegos/suguru/1" replace />} />
            <Route path="juegos/mastermind/:dificultad" element={<MastermindGame />} />
            <Route path="juegos/mastermind" element={<Navigate to="/juegos/mastermind/1" replace />} />
            <Route path="juegos/dueto/:dificultad" element={<DuetoGame />} />
            <Route path="juegos/dueto" element={<Navigate to="/juegos/dueto/1" replace />} />
            <Route path="juegos/convergencia/:dificultad" element={<ConvergenciaGame />} />
            <Route path="juegos/convergencia" element={<Navigate to="/juegos/convergencia/1" replace />} />
            <Route path="juegos/flechas/:dificultad" element={<FlechasGame />} />
            <Route path="juegos/flechas" element={<Navigate to="/juegos/flechas/1" replace />} />
            <Route path="juegos/vecinos/:dificultad" element={<VecinosGame />} />
            <Route path="juegos/vecinos" element={<Navigate to="/juegos/vecinos/1" replace />} />
            <Route path="juegos/itv-express/:dificultad" element={<ITVExpressGame />} />
            <Route path="juegos/itv-express" element={<Navigate to="/juegos/itv-express/1" replace />} />
            <Route path="juegos/sashigane/:dificultad" element={<SashiganeGame />} />
            <Route path="juegos/sashigane" element={<Navigate to="/juegos/sashigane/1" replace />} />
            <Route path="juegos/trias/:dificultad" element={<TriasGame />} />
            <Route path="juegos/trias" element={<Navigate to="/juegos/trias/1" replace />} />
            <Route path="juegos/cine/:dificultad" element={<CineGame />} />
            <Route path="juegos/cine" element={<Navigate to="/juegos/cine/1" replace />} />

            {/* Kids Games */}
            <Route path="juegos/memoria" element={<MemoriaGame />} />
            <Route path="juegos/simon" element={<SimonGame />} />
            <Route path="juegos/mastermind-kids" element={<MastermindKidsGame />} />
            <Route path="juegos/sudoku-kids" element={<SudokuKidsGame />} />
            <Route path="juegos/laberinto" element={<LaberintoGame />} />
            <Route path="juegos/diferencias" element={<DiferenciasGame />} />
            <Route path="juegos/tetravex/:dificultad" element={<TetravexGame />} />
            <Route path="juegos/tetravex" element={<Navigate to="/juegos/tetravex/1" replace />} />
            <Route path="juegos/frito-variado/:dificultad" element={<FritoVariadoGame />} />
            <Route path="juegos/frito-variado" element={<Navigate to="/juegos/frito-variado/1" replace />} />
            <Route path="juegos/tasukuea/:dificultad" element={<TasukueaGame />} />
            <Route path="juegos/tasukuea" element={<Navigate to="/juegos/tasukuea/1" replace />} />
            <Route path="juegos/senderos/:dificultad" element={<SenderosGame />} />
            <Route path="juegos/senderos" element={<Navigate to="/juegos/senderos/1" replace />} />
            <Route path="juegos/norinori/:dificultad" element={<NorinoriGame />} />
            <Route path="juegos/norinori" element={<Navigate to="/juegos/norinori/1" replace />} />
            <Route path="juegos/nexo" element={<NexoGame />} />
            <Route path="juegos/tetonor/:dificultad" element={<TetonorGame />} />
            <Route path="juegos/tetonor" element={<Navigate to="/juegos/tetonor/1" replace />} />
            <Route path="juegos/fillomino/:dificultad" element={<FillominoGame />} />
            <Route path="juegos/fillomino" element={<Navigate to="/juegos/fillomino/1" replace />} />
            <Route path="juegos/estanque/:dificultad" element={<EstanqueGame />} />
            <Route path="juegos/estanque" element={<Navigate to="/juegos/estanque/1" replace />} />

            <Route path="progreso" element={<ProgressPage />} />
            <Route path="perfil" element={<ProfilePage />} />
            <Route path="rutina" element={<DailyPage />} />
            <Route path="profesional" element={<ProfessionalDashboardPage />} />
            <Route path="profesional/estadisticas" element={<ProfessionalStatsPage />} />
            <Route path="profesional/pacientes" element={<ProfessionalPatientsPage />} />
            <Route path="profesional/sesiones" element={<ProfessionalSessionsPage />} />
            <Route path="profesional/sesiones/nueva" element={<CreateSessionPage />} />
            <Route path="profesional/sesiones/presencial/:id" element={<ActivePresencialSessionPage />} />
            <Route path="profesional/sesiones/:id" element={<SessionDetailPage />} />
            <Route path="paciente/sesiones" element={<PatientSessionsPage />} />
            <Route path="paciente/sesiones/:id" element={<PatientSessionDetailPage />} />
            <Route path="herramientas" element={<HerramientasLibraryPage />} />
            <Route path="herramientas/crear" element={<HerramientasCreatePage />} />
            <Route path="herramientas/editar/:id" element={<HerramientasEditPage />} />
            <Route path="herramientas/sesion/:id" element={<HerramientasSessionPage />} />
            <Route path="herramientas/fichas" element={<HerramientasFichasPage />} />

            <Route path="admin" element={<AdminDashboardPage />} />
            <Route path="admin/estadisticas" element={<AdminStatsPage />} />
            <Route path="mi-progreso" element={<UserStatsPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="bottom-right"
        theme="dark"
        richColors
      />
    </TenantProvider>
    </AccessibilityProvider>
  );
}

export default App;
